/**
 * NERC CIP-007 R2 patch management (point 4): labels and the few checks the forms make before saving.
 * The backend (NercPatchRules, NercPatchService) is the authority; its message is shown whenever it refuses.
 * Dates are calendar dates in the organisation's NERC time zone: the forms use the server's "today".
 */

export const PATCH_STATE_LABELS = {
  Pending: { text: "Pending", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  Applied: { text: "Applied", tone: "bg-green-50 text-green-700 border-green-200" },
  NotApplicable: { text: "Not applicable", tone: "bg-gray-100 text-gray-700 border-gray-200" },
  Mitigated: { text: "Under a plan", tone: "bg-blue-50 text-blue-700 border-blue-200" },
};

export const APPROVAL_LABELS = {
  NotRequired: "In force",
  Pending: "Waiting for approval",
  Approved: "Approved",
  Rejected: "Rejected",
};

export const VERSION_KIND_LABELS = {
  Initial: "Plan created",
  ScopeRevision: "Patches added",
  Progress: "Progress",
  Revision: "Revision",
  Extension: "Extension",
};

export const OUTCOME_LABELS = {
  PatchesFound: "Patches found",
  NoNewPatches: "No new patches",
  SourceUnavailable: "Source unavailable",
  Incomplete: "Incomplete",
};

export const STATEMENT_REASON_LABELS = {
  NoSourceExists: "No patch source exists",
  NotUpdateable: "Not updateable",
};

export const DUE_ITEM_LABELS = {
  Evaluation: "Evaluation",
  Decision: "Patch decision",
  Plan: "Mitigation plan",
  Extension: "Approval request",
};

/** The four CIP-007 R2 parts as the readiness strip names them. */
export const REQUIREMENT_LABELS = {
  "CIP-007 R2.1": "Patch sources identified",
  "CIP-007 R2.2": "Evaluated every 35 days",
  "CIP-007 R2.3": "Decided within 35 days",
  "CIP-007 R2.4": "Plans on time",
};

export const CVE_ID = /^CVE-\d{4}-\d{4,7}$/i;

/** CVE ids typed into one field, separated by commas or spaces. */
export const parseCves = (text) => String(text || "").split(/[\s,;]+/).map((c) => c.trim()).filter(Boolean);

/** The patch identity key (vendor + identifier), as the server compares it: trimmed, spaces collapsed, any case. */
export const identityKey = (value) => String(value || "").trim().replace(/\s+/g, " ").toUpperCase();

/** One patch row of the evaluation form, empty; the vendor starts as the source's. */
export const emptyPatchRow = (vendor = "") => ({
  vendor: vendor || "", identifier: "", title: "", product: "", releasedOn: "", cves: "", applicability: "Applicable", reason: "",
});

/** The evaluation form's own checks (the server checks again and says more). */
export function patchRowProblems(rows) {
  const problems = [];
  const seen = new Set();
  rows.forEach((r, i) => {
    const n = `Patch ${i + 1}:`;
    if (!r.vendor.trim()) problems.push(`${n} the vendor is required.`);
    if (!r.identifier.trim()) problems.push(`${n} the identifier is required.`);
    else {
      const key = `${identityKey(r.vendor)}|${identityKey(r.identifier)}`;
      if (seen.has(key)) problems.push(`${n} the same patch (vendor and identifier) is listed twice.`);
      seen.add(key);
    }
    if (!r.title.trim()) problems.push(`${n} the title is required.`);
    if (r.applicability === "NotApplicable" && !r.reason.trim()) problems.push(`${n} say why it is not applicable.`);
    if (parseCves(r.cves).some((c) => !CVE_ID.test(c))) problems.push(`${n} each CVE id must look like CVE-2026-12345.`);
  });
  return problems;
}

/** A row as the API takes it. */
export const rowToRequest = (r) => ({
  vendor: r.vendor.trim() || null, identifier: r.identifier, title: r.title, product: r.product.trim() || null,
  releasedOn: r.releasedOn || null, cveIds: parseCves(r.cves), applicability: r.applicability, reason: r.reason || null,
});

/** A Defender CVE as a prefilled patch row: the evaluator still names the vendor's fix. */
export const rowFromCve = (cve, vendor = "") => ({
  ...emptyPatchRow(vendor || cve.softwareVendor || ""),
  identifier: cve.cveId,
  title: [`Fix for ${cve.cveId}`, cve.softwareName && `in ${[cve.softwareName, cve.softwareVersion].filter(Boolean).join(" ")}`].filter(Boolean).join(" "),
  product: cve.softwareName || "",
  cves: cve.cveId,
});

/** "Pending" pairs of a patch on in-scope assets: what a new mitigation plan may cover. */
export const pendingStates = (patch) => (patch.assets || []).filter((a) => a.inScope && a.state === "Pending");

/** Pending pairs that may still join an existing plan: on or before their own decision date (R2.3 "revise a plan"). */
export const pendingWithinDecision = (patch, today) =>
  pendingStates(patch).filter((a) => !a.decisionDueOn || !today || today <= a.decisionDueOn);

const FALLBACK_TIME_ZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage",
  "Pacific/Honolulu", "America/Halifax", "America/Toronto", "America/Winnipeg", "America/Edmonton", "America/Vancouver",
  "America/Mexico_City", "UTC",
];

/** IANA time zones the browser knows, or a North American list when it cannot say. */
export function timeZoneOptions() {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      const zones = Intl.supportedValuesOf("timeZone");
      return zones.includes("UTC") ? zones : [...zones, "UTC"];
    }
  } catch { /* older browsers */ }
  return FALLBACK_TIME_ZONES;
}
